import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BottomSheet — web implementation.
 *
 * Renders a sheet that slides up from the bottom of the viewport.
 * Snap points are expressed as viewport height percentages: 25 | 50 | 90.
 * On mobile web this component handles swipe-to-dismiss via pointer events.
 *
 * Design reference: Figma "BottomSheet — Snap Points & Gestures (Mobile)"
 * node-id=4752:1815
 *
 * Theming (WEB-286): surface chroma is resolved through CSS custom properties
 * (`--color-background`, `--color-foreground`, `--color-muted-foreground`,
 * `--color-border`) so the panel inherits the active theme from the
 * `<html data-theme="…">` root. Prior to WEB-286 the panel hard-coded
 * `#ffffff`, which made `text-foreground` headings invisible in dark mode
 * (white-on-white) and silently bypassed every theme flip. Each token retains
 * its previous literal as a fallback so light-mode rendering is identical for
 * consumers that opt out of the theme system.
 */
import { forwardRef, useEffect, useRef, useState, useCallback, useId } from 'react';
import { X } from 'lucide-react';
import { Portal } from './Portal';
import { useFocusTrap } from './hooks/useFocusTrap';
import { useScrollLock } from './hooks/useScrollLock';
// ---------------------------------------------------------------------------
// Animation keyframes (injected once)
// ---------------------------------------------------------------------------
const ANIM_ID = 'loci-bottomsheet-keyframes';
function injectKeyframes() {
    if (typeof document === 'undefined' || document.getElementById(ANIM_ID))
        return;
    const style = document.createElement('style');
    style.id = ANIM_ID;
    style.textContent = `
    @keyframes fs-sheet-in {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }
    @keyframes fs-sheet-out {
      from { transform: translateY(0); }
      to   { transform: translateY(100%); }
    }
    @keyframes fs-backdrop-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `;
    document.head.appendChild(style);
}
if (typeof window !== 'undefined') {
    injectKeyframes();
}
// ---------------------------------------------------------------------------
// Swipe detection constants
// Figma spec: 500px/s velocity triggers dismiss
// ---------------------------------------------------------------------------
const DISMISS_VELOCITY_PX_S = 500;
const DISMISS_DISTANCE_RATIO = 0.25; // dismiss if dragged > 25% of sheet height
// ---------------------------------------------------------------------------
// BottomSheet
// ---------------------------------------------------------------------------
export const BottomSheet = forwardRef(({ open, onClose, snapPoint = 50, snapPoints = [25, 50, 90], closeOnBackdropClick = true, closeOnEscape = true, showHandle = true, showCloseButton = true, header, children, footer, onSnapPointChange, className, style, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, }, ref) => {
    const generatedId = useId();
    const labelId = ariaLabelledBy ?? generatedId;
    const { containerRef } = useFocusTrap({ active: open });
    useScrollLock(open);
    // Drag state
    const dragStartYRef = useRef(0);
    const dragStartTimeRef = useRef(0);
    const isDraggingRef = useRef(false);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const sheetHeight = `${snapPoint}vh`;
    // Snap to the nearest point after drag ends
    const snapToNearest = useCallback((dragDelta, velocity, sheetHeightPx) => {
        const draggedRatio = dragDelta / sheetHeightPx;
        const fastSwipeDown = velocity > DISMISS_VELOCITY_PX_S && dragDelta > 0;
        const bigSwipeDown = draggedRatio > DISMISS_DISTANCE_RATIO;
        if (fastSwipeDown || bigSwipeDown) {
            const currentIndex = snapPoints.indexOf(snapPoint);
            if (currentIndex <= 0) {
                onClose();
            }
            else {
                const nextPoint = snapPoints[currentIndex - 1];
                onSnapPointChange?.(nextPoint);
            }
        }
        else if (dragDelta < -50) {
            const currentIndex = snapPoints.indexOf(snapPoint);
            const nextPoint = snapPoints[Math.min(currentIndex + 1, snapPoints.length - 1)];
            onSnapPointChange?.(nextPoint);
        }
    }, [snapPoints, snapPoint, onClose, onSnapPointChange]);
    // Pointer event handlers for drag-to-dismiss
    const handlePointerDown = useCallback((e) => {
        isDraggingRef.current = true;
        dragStartYRef.current = e.clientY;
        dragStartTimeRef.current = performance.now();
        e.currentTarget.setPointerCapture(e.pointerId);
    }, []);
    const handlePointerMove = useCallback((e) => {
        if (!isDraggingRef.current)
            return;
        const delta = e.clientY - dragStartYRef.current;
        setDragOffsetY(Math.max(0, delta));
    }, []);
    const handlePointerUp = useCallback((e) => {
        if (!isDraggingRef.current)
            return;
        isDraggingRef.current = false;
        const delta = e.clientY - dragStartYRef.current;
        const elapsed = (performance.now() - dragStartTimeRef.current) / 1000;
        const velocity = Math.abs(delta) / elapsed;
        const sheetEl = containerRef.current;
        const sheetHeightPx = sheetEl?.clientHeight ?? window.innerHeight * (snapPoint / 100);
        snapToNearest(delta, velocity, sheetHeightPx);
        setDragOffsetY(0);
    }, [snapToNearest, snapPoint, containerRef]);
    // Escape key
    useEffect(() => {
        if (!open || !closeOnEscape)
            return;
        const handle = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', handle);
        return () => document.removeEventListener('keydown', handle);
    }, [open, closeOnEscape, onClose]);
    if (!open)
        return null;
    const translateY = dragOffsetY > 0 ? `translateY(${dragOffsetY}px)` : undefined;
    return (_jsxs(Portal, { children: [_jsx("div", { style: {
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1000,
                    animation: 'fs-backdrop-in 200ms ease',
                }, onClick: closeOnBackdropClick ? onClose : undefined, "data-testid": "bottomsheet-backdrop" }), _jsxs("div", { ref: (node) => {
                    containerRef.current = node;
                    if (typeof ref === 'function')
                        ref(node);
                    else if (ref)
                        ref.current = node;
                }, className: className, style: {
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: sheetHeight,
                    // WEB-286: theme-aware surface — falls back to the previous
                    // literal so light-mode rendering is byte-identical for consumers
                    // that opt out of the theme system.
                    backgroundColor: 'var(--color-background, #ffffff)',
                    color: 'var(--color-foreground, inherit)',
                    borderRadius: '16px 16px 0 0',
                    boxShadow: '0px -4px 20px 0px rgba(0,0,0,0.12)',
                    zIndex: 1001,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    animation: `fs-sheet-in 300ms cubic-bezier(0.32,0.72,0,1)`,
                    transform: translateY,
                    transition: dragOffsetY === 0 ? 'transform 300ms cubic-bezier(0.32,0.72,0,1)' : 'none',
                    outline: 'none',
                    ...style,
                }, tabIndex: -1, role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, "aria-labelledby": ariaLabel ? undefined : labelId, "data-testid": "bottomsheet-panel", children: [_jsx("div", { style: {
                            padding: '12px 16px 0',
                            cursor: 'grab',
                            flexShrink: 0,
                            touchAction: 'none',
                        }, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, "aria-hidden": "true", children: showHandle && (_jsx("div", { style: {
                                width: 36,
                                height: 4,
                                // WEB-286: themed handle — `--color-border` resolves to a
                                // light gray in light mode (#e4e8f0) and an indigo gray in
                                // dark mode (#2a3142), so the handle stays visible against
                                // the active sheet surface.
                                backgroundColor: 'var(--color-border, #d1d5db)',
                                borderRadius: 9999,
                                margin: '0 auto',
                            }, "data-testid": "bottomsheet-handle" })) }), (header ?? showCloseButton) && (_jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px 0',
                            flexShrink: 0,
                        }, children: [_jsx("div", { style: { flex: 1 }, children: header }), showCloseButton && (_jsx("button", { type: "button", onClick: onClose, "aria-label": "Close", style: {
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    // WEB-286: themed close-icon color — `--color-muted-foreground`
                                    // is WCAG-AA on both surfaces (≈4.6:1 light, ≥7:1 dark).
                                    color: 'var(--color-muted-foreground, #6b7280)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    lineHeight: 0,
                                    minWidth: 44,
                                    minHeight: 44,
                                }, children: _jsx(X, { size: 20, strokeWidth: 2 }) }))] })), _jsx("div", { style: {
                            flex: 1,
                            overflowY: 'auto',
                            padding: '16px 20px',
                            WebkitOverflowScrolling: 'touch',
                        }, children: children }), footer && (_jsx("div", { style: {
                            padding: '12px 20px 20px',
                            flexShrink: 0,
                            // WEB-286: themed footer divider — pairs with the panel
                            // surface so the rule is visible in both themes.
                            borderTop: '1px solid var(--color-border, #f3f4f6)',
                        }, children: footer }))] })] }));
});
BottomSheet.displayName = 'BottomSheet';
